import {
  ChangeDetectorRef,
  Component,
  HostListener,
  NgZone,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EventType, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/auth/auth.service';
import { CurrentUserService } from '../../core/services/auth/current-user.service';
import { CommonModule } from '@angular/common';
import { environment } from '../../../environments/environment';
import { UserService } from '../../core/services/user/user.service';
import { NotificationList } from '../../core/models/notification/notification-list.model';
import { StatusUtils } from '../../utils/status-utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CookieService } from '../../core/services/cookie.service';
import { UserSessionModel } from '../../core/models/user/current-user.model';
import { Subscription } from 'rxjs';
import { UserRole } from '../../../enums/roles.enum';
import { menuTopItems as rawMenuTopItems } from '../../main/constants/menu-items';
import { PermissionService } from '../../core/services/user/permission.service';
import { VersionService } from '../../core/services/version/version.service';
import { NotificationSignalRService } from 'app/main/shared/services/notification-signalr.service';
import { NotificationModel } from 'app/core/models/events/notification.model';
import { NotificationService } from 'app/core/services/notification/notification.service';
import { HasClaimDirective } from 'app/shared/directives/has-claim.directive';
import { OrganizationService } from '../../core/services/organization/organization.service';

declare var KTComponents: any;
declare var KTDrawer: any;

@Component({
  selector: 'app-sidebar',

  standalone: true,
  imports: [RouterModule, CommonModule, TranslateModule, HasClaimDirective],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  constructor(
    private notifHub: NotificationSignalRService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private currentUserService: CurrentUserService,
    private translate: TranslateService,
    private cookieService: CookieService,
    private router: Router,
    private permissionService: PermissionService,
    private userService: UserService,
    private organizationService: OrganizationService,
    private http: HttpClient,
    private versionService: VersionService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
    this.routerSubscribe();
    const userLang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(userLang);
  }

  menuTopItems: any[] = [];
  menuBottomItems = [];
  currentUser: UserSessionModel = null;
  notificationList: NotificationList[] = [];
  unReadNotificationMessageCount: number = 0;
  statusClass: string;
  isSingleApplication: boolean;
  userAvatar: string | null = null;
  orgAvatar: string | null = null;
  orgName: string | null = null;
  currentYear: number = new Date().getFullYear();
  version: string = 'v1.1.0';
  userRole: any = UserRole;
  isFooterHidden: boolean = true;
  isSidebarHovered: boolean = false;
  isSidebarMinimized: boolean = false;

  isNotificationCardOpen = false;
  notificationCardStyle: any = {};

  notifications: NotificationModel[] = [];
  unreadCount = 0;
  private sub = new Subscription();

  private subscription: Subscription;
  private permissionsSubscription: Subscription;
  private overlayObserver: MutationObserver;

  ngAfterViewInit() {
    this.applyStoredTheme();
    this.syncSidebarState();
    this.syncFooterVisibility();
    setTimeout(() => {
      KTComponents?.init();
      this.initializeMenu();
      this.setupToggleHandler();
    }, 200);
  }

  setupToggleHandler() {
    const toggleBtn = document.getElementById('kt_app_sidebar_toggle');
    const sidebar = document.getElementById('kt_app_sidebar');
    const body = document.body;
    const isTouchDevice = () => {
      return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        (navigator as any).msMaxTouchPoints > 0
      );
    };

    if (toggleBtn && sidebar) {
      toggleBtn.addEventListener('click', () => {
        // Sync sidebar minimized state after toggle
        setTimeout(() => {
          this.syncSidebarState();
          this.syncFooterVisibility();
        }, 50);

        body.setAttribute('data-kt-app-sidebar-hoverable', 'false');

        const mouseLeaveHandler = () => {
          body.setAttribute('data-kt-app-sidebar-hoverable', 'true');
          sidebar.removeEventListener('mouseleave', mouseLeaveHandler);
        };

        sidebar.addEventListener('mouseleave', mouseLeaveHandler);
      });

      // Add hover listeners for footer text animation (only on non-touch devices)
      if (!isTouchDevice()) {
        sidebar.addEventListener('mouseenter', () => {
          this.isSidebarHovered = true;
        });

        sidebar.addEventListener('mouseleave', () => {
          this.isSidebarHovered = false;
        });
      }
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.subscription?.unsubscribe();
    this.permissionsSubscription?.unsubscribe();
    if (this.overlayObserver) {
      this.overlayObserver.disconnect();
    }
  }

  async ngOnInit() {
    // Sync footer visibility and sidebar state with current DOM state before template renders
    const body = document.body;
    this.isFooterHidden = body.hasAttribute('data-kt-app-sidebar-minimize');
    this.isSidebarMinimized = body.hasAttribute('data-kt-app-sidebar-minimize');

    await StatusUtils?.initializeTranslateService(this.translate);
    // this.fetchLatestVersion(); // Removed, handled by VersionService subscription
    this.versionService.version$.subscribe((v) => {
      if (v) this.version = v;
    });
    this.loadUserDataAndMenu();

    // Subscribe to permission changes to update menu when user logs in/out
    this.permissionsSubscription =
      this.permissionService.permissionsChanged$.subscribe(() => {
        this.loadUserDataAndMenu();
      });

    this.overlayObserver = new MutationObserver((mutations) => {
      let checkNeeded = false;
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            const node = mutation.addedNodes[i] as HTMLElement;
            if (node.classList && node.classList.contains('drawer-overlay')) {
              checkNeeded = true;
              break;
            }
          }
        }
      }

      if (checkNeeded) {
        const overlays = document.querySelectorAll('.drawer-overlay');
        if (overlays.length > 1) {
          for (let i = 0; i < overlays.length - 1; i++) {
            overlays[i].remove();
          }
        }
      }
    });

    this.overlayObserver.observe(document.body, {
      childList: true,
    });

    this.currentUserService.avatarChanged$.subscribe((avatar) => {
      this.userAvatar = avatar;
    });

    const userId = this.currentUserService.GetSession().id; // sende nasıl alıyorsan

    this.sub.add(
      this.notifHub.notifications$.subscribe((list) => {
        this.notifications = list;
        this.cdr.detectChanges();
      }),
    );

    this.sub.add(
      this.notifHub.unreadCount$.subscribe((c) => {
        this.unreadCount = c;
        this.cdr.detectChanges();
      }),
    );

    this.notifHub.connectToHub(
      environment.hubs.notificationHub.url,
      `/${environment.hubs.notificationHub.name}`,
      userId,
    );

    this.notifHub.connectToHub(
      environment.hubs.communicationHub.url,
      `/${environment.hubs.communicationHub.name}`,
      userId,
    );

    this.notifHub.connectToHub(
      environment.hubs.woundHub.url,
      `/${environment.hubs.woundHub.name}`,
      userId,
    );
  }

  private loadUserDataAndMenu() {
    this.currentUser = this.currentUserService.GetSession();
    if (this.currentUser) {
      console.log(this.currentUser.role.name);

      this.loadUserAvatar();
      this.loadOrgAvatar();
    }

    this.menuTopItems = rawMenuTopItems.filter((item) => {
      if (!item.permissions || item.permissions.length === 0) {
        return true;
      }
      return this.permissionService.hasAnyPermission(item.permissions);
    });

    this.statusClass = StatusUtils?.getUserStatusClass(
      this.currentUser?.status,
    );
    this.isSingleApplication =
      this.cookieService.getCookie('applications') != null
        ? JSON.parse(this.cookieService.getCookie('applications')).length == 1
        : true;

    this.menuBottomItems = [
      {
        condition: true,
        routerLink: '/profile/detail',
        icon: null,
        avatar: true,
        title: this.currentUser
          ? `${this.currentUser.name} ${this.currentUser.lastName}`
          : '',
      },
      {
        condition: this.currentUser?.role?.name === this.userRole.Admin,
        routerLink: null,
        icon: 'fa-solid fa-gear',
        click: 'navigateToSettings',
        title: 'settings',
      },
      {
        condition: true,
        routerLink: '/infoPage/info',
        icon: 'fa-solid fa-circle-info',
        title: 'info',
      },
      {
        condition: true,
        routerLink: null,
        icon: 'fa fa-sign-out',
        click: 'signOut',
        title: 'logout',
      },
    ];
  }

  onNotificationClick(n: NotificationModel) {
    if (n.targetUrl && n.id) {
      this.notificationService
        .markNotificationsAsRead({
          id: n.id,
        })
        .subscribe((res) => {
          if (res.data.isSuccess) {
            this.notifHub.removeFromList(n.id);
            this.router.navigateByUrl(n.targetUrl);
          }
        });
    }
    this.isNotificationCardOpen = false;
  }

  onShowAllNotification() {
    this.router.navigateByUrl('/notifications/notifications');
  }

  toggleNotificationCard(event: MouseEvent) {
    event.stopPropagation();
    this.isNotificationCardOpen = !this.isNotificationCardOpen;

    if (!this.isNotificationCardOpen) return;

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    const isMinimized = document.body.hasAttribute(
      'data-kt-app-sidebar-minimize',
    );

    const CARD_HEIGHT = 380;
    const CARD_WIDTH = 360;
    const VIEWPORT_PADDING = 12;
    const SIDEBAR_WIDTH = isMinimized ? 80 : 155;

    let left = SIDEBAR_WIDTH;
    if (left + CARD_WIDTH > window.innerWidth) {
      left = window.innerWidth - CARD_WIDTH - VIEWPORT_PADDING;
    }

    const iconCenterFromBottom =
      window.innerHeight - (rect.top + rect.height / 2);

    let bottomValue = iconCenterFromBottom - CARD_HEIGHT / 2;

    const minBottom = VIEWPORT_PADDING;
    const maxBottom = window.innerHeight - CARD_HEIGHT - VIEWPORT_PADDING;

    bottomValue = Math.max(minBottom, Math.min(bottomValue, maxBottom));

    const cardTop = window.innerHeight - (bottomValue + CARD_HEIGHT);
    const iconCenterRelative = rect.top + rect.height / 2 - cardTop;

    this.notificationCardStyle = {
      left: `${left}px`,
      bottom: `${bottomValue}px`,
      '--arrow-top': `${iconCenterRelative}px`,
    };
  }

  @HostListener('document:click')
  closeOnOutsideClick() {
    this.isNotificationCardOpen = false;
  }

  trackById(index: number, item: any) {
    return item.id;
  }

  private loadUserAvatar(): void {
    if (this.currentUserService.isAvatarCached()) {
      if (this.currentUserService.hasNoAvatar()) {
        this.userAvatar = null;
      } else {
        this.userAvatar = this.currentUserService.getCachedAvatar();
      }
      return;
    }

    if (!this.currentUser?.id) {
      this.userAvatar = null;
      return;
    }

    this.userService.getAvatar(this.currentUser.id).subscribe({
      next: (res) => {
        if (res?.isSuccess && res.data?.avatar) {
          const avatar = res.data.avatar.startsWith('data:image')
            ? res.data.avatar
            : `data:image/jpeg;base64,${res.data.avatar}`;
          this.userAvatar = avatar;
          this.currentUserService.setCachedAvatar(avatar);
        } else {
          this.userAvatar = null;
          this.currentUserService.setCachedAvatar(null);
        }
      },
      error: () => {
        this.userAvatar = null;
      },
    });
  }

  private loadOrgAvatar(): void {
    const orgs = this.currentUser?.userOrganizations;
    if (!orgs || orgs.length === 0) return;

    const orgId = orgs[0].organizationId;
    this.orgName = orgs[0].organizationName;

    this.organizationService.getAvatar(orgId).subscribe({
      next: (res) => {
        if (res?.isSuccess && res.data?.avatar) {
          this.orgAvatar = res.data.avatar.startsWith('data:image')
            ? res.data.avatar
            : `data:image/jpeg;base64,${res.data.avatar}`;
        } else {
          this.orgAvatar = null;
        }
      },
      error: () => {
        this.orgAvatar = null;
      },
    });
  }

  initializeMenu() {
    const menuItems = document.querySelectorAll(
      '[data-kt-menu-trigger="click"]',
    );
    menuItems.forEach((item) => {
      const menuLink = item.querySelector('.menu-link') as HTMLElement;
      const menuSub = item.querySelector('.menu-sub') as HTMLElement;

      if (menuLink) {
        menuLink.addEventListener('click', (event) => {
          event.stopPropagation();
          event.preventDefault();
          if (menuSub) {
            if (menuSub.style.display === 'block') {
              menuSub.style.display = 'none';
            } else {
              const openMenus = document.querySelectorAll('.menu-sub');
              openMenus.forEach((openMenu) => {
                (openMenu as HTMLElement).style.display = 'none';
              });
              menuSub.style.display = 'block';
            }
          }
        });
      }

      if (menuSub) {
        const subMenuLinks = menuSub.querySelectorAll('.menu-item a');
        subMenuLinks.forEach((subMenuLink) => {
          subMenuLink.addEventListener('click', (event) => {
            event.stopPropagation();
          });
        });
      }
    });
  }

  setTheme(mode: string) {
    localStorage.setItem('data-bs-theme', mode);
    localStorage.setItem('data-bs-theme-mode', mode);
    document.documentElement.setAttribute('data-bs-theme', mode);
  }

  private applyStoredTheme(): void {
    const storedTheme = localStorage.getItem('data-bs-theme');
    if (storedTheme) {
      document.documentElement.setAttribute('data-bs-theme', storedTheme);
    }
  }

  showLogoutConfirmation() {
    const modalElement = document.getElementById('logoutConfirmationModal');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  confirmLogout() {
    this.authService.logout().subscribe(() => {
      localStorage.setItem('isLogin', 'false');
    });
  }

  routerSubscribe() {
    this.subscription?.unsubscribe();
    this.subscription = this.router.events.subscribe((event) => {
      if (
        event.type === EventType.NavigationEnd ||
        event.constructor.name === 'NavigationEnd'
      ) {
        this.closeSidebar(event['url']);
        this.closeDrawer();
        // Sync footer visibility and hover state after navigation
        setTimeout(() => {
          this.syncFooterVisibility();
          this.isSidebarHovered = false;
        }, 50);
      }
    });
  }

  /**
   * @param url: string
   *
   * Add route name in this array if you want to close sidebar after route change
   * @const closeList: Array<string>;
   */
  closeSidebar(url: string) {
    const closeList = ['/consultation/detail'];
    const isClosed = document.body.hasAttribute('data-kt-app-sidebar-minimize');
    if (isClosed || !url || typeof url !== 'string') return;
    const hasRoute = closeList.find((x) => !!url.includes(x));
    if (!hasRoute) return;
    const toggleBtnElement = document.getElementById('kt_app_sidebar_toggle');
    toggleBtnElement?.click();
  }

  closeDrawer() {
    const sidebarElement = document.getElementById('kt_app_sidebar');
    if (sidebarElement && typeof KTDrawer !== 'undefined') {
      const drawer = KTDrawer.getInstance(sidebarElement);
      if (drawer) {
        drawer.hide();
      }
    }
  }

  toggleFooterVisibility() {
    this.isFooterHidden = !this.isFooterHidden;
  }

  syncSidebarState() {
    const body = document.body;
    this.isSidebarMinimized = body.hasAttribute('data-kt-app-sidebar-minimize');
  }

  private syncFooterVisibility() {
    const body = document.body;
    this.isFooterHidden = body.hasAttribute('data-kt-app-sidebar-minimize');
  }

  /* fetchLatestVersion logic moved to VersionService */
}
